import React, { useEffect, useState, useCallback } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../../store/api/fbConfig';
import {
  collection, query, where, or, getDocs, addDoc, updateDoc, doc
} from 'firebase/firestore';

import {
  List, ListItem, ListItemText, Divider, Typography, Paper,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, CircularProgress, Box
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';

const GroupSideBar = () => {
  const { id: groupId } = useParams();
  const [groups, setGroups] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const [addOpen, setAddOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [editGroupName, setEditGroupName] = useState('');

  const loadGroups = useCallback(async (currentUser) => {
    setLoading(true);
    const groupRef = collection(db, 'Groups');
    const q = query(
      groupRef,
      or(
        where('owner', '==', currentUser.uid),
        where(`users.${currentUser.uid}`, 'in', ["viewer", "editor", "admin"])
      )
    );
    const snap = await getDocs(q);
    const groupList = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setGroups(groupList);
    setLoading(false);
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        loadGroups(firebaseUser);
      }
    });
    return () => unsub();
  }, [loadGroups]);

  const handleAddGroup = async () => {
    if (!newGroupName.trim() || !user) return;

    setLoading(true);
    await addDoc(collection(db, 'Groups'), {
      name: newGroupName.trim(),
      owner: user.uid,
      users: {},
      createdAt: new Date()
    });
    setNewGroupName('');
    setAddOpen(false);
    await loadGroups(user);
  };

  const handleEditClick = (group) => {
    setEditGroup(group);
    setEditGroupName(group.name);
    setEditOpen(true);
  };

  const handleEditGroup = async () => {
    if (!editGroupName.trim()) return;
    await updateDoc(doc(db, 'Groups', editGroup.id), {
      name: editGroupName.trim()
    });
    setEditOpen(false);
    setEditGroup(null);
    await loadGroups(user);
  };

  return (
    <Paper elevation={1} sx={{ padding: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h6">Groups</Typography>
        <Box>
          {groupId && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                const currentGroup = groups.find(g => g.id === groupId);
                if (currentGroup) handleEditClick(currentGroup);
              }}
              title="Edit Group"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={() => setAddOpen(true)}
            title="Add Group"
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" my={2}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <List dense>
          <ListItem
            button
            component={NavLink}
            to="/devices"
            selected={!groupId}
          >
            <ListItemText primary="All Devices" />
          </ListItem>
          <Divider />
          {groups.map(group => {
            const isCurrent = group.id === groupId;
            return (
              <ListItem
                button
                key={group.id}
                component={NavLink}
                to={`/group/${group.id}`}
                selected={isCurrent}
              >
                <ListItemText
                  primary={
                    <span style={{ fontWeight: isCurrent ? 'bold' : 'normal' }}>
                      {group.name}
                    </span>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      )}

      {/* Add Group Dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)}>
        <DialogTitle>Add New Group</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Group Name"
            fullWidth
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddGroup}
            disabled={!newGroupName.trim()}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Group Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)}>
        <DialogTitle>Edit Group Name</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Group Name"
            fullWidth
            value={editGroupName}
            onChange={(e) => setEditGroupName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditGroup}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default GroupSideBar;

